// src/services/dayjs-extended.ts
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
var dayjs_extended_default = dayjs;

// src/services/vy-date.ts
import { isString } from "@vy/string";
import { isBoolean } from "@vy/boolean";
function vyDate(date, options = {}) {
  const { format, keepLocalTime, localeOrStrict, strict } = {
    keepLocalTime: false,
    localeOrStrict: false,
    strict: false,
    ...options
  };
  const locale = isString(localeOrStrict) ? localeOrStrict : void 0;
  const strictValue = isBoolean(localeOrStrict) ? localeOrStrict : strict;
  let time = dayjs_extended_default(date, format, locale, strictValue);
  if (!time.isUTC()) {
    time = time.utc(keepLocalTime);
  }
  return time;
}

// src/services/date-constants.ts
function now() {
  return vyDate().toDate();
}
function yesterday() {
  return dayBefore(now());
}
function dayBefore(date) {
  return vyDate(date).subtract(1, "day");
}
function tomorrow() {
  return dayAfter(now());
}
function dayAfter(date) {
  return vyDate(date).add(1, "day");
}
function startOfDay(date) {
  return vyDate(date).startOf("day");
}
function endOfDay(date) {
  return vyDate(date).endOf("day");
}
function startOfMonth(date) {
  return vyDate(date).startOf("month");
}
function endOfMonth(date) {
  return vyDate(date).endOf("month");
}
function subtractDays(date, days) {
  return vyDate(date).subtract(days, "day").toDate();
}

// src/services/date-conditions.ts
function isInTheFuture(date) {
  return vyDate(date).isAfter(now());
}
function isInThePast(date) {
  return vyDate(date).isBefore(now());
}
function isDate(date) {
  return date instanceof Date;
}
function isSameDate(date1, date2) {
  return date1.getTime() === date2.getTime();
}

// src/services/date-formatter.ts
function formatDate(date, dateFormat = "MMM. D, YY") {
  return vyDate(date).format(dateFormat);
}
function formatDateTime64(date) {
  return date.toISOString();
}

// src/services/date-operations.ts
import { first, max, min } from "lodash";
function getMinDate(...dates) {
  return min(dates) ?? first(dates);
}
function getMaxDate(...dates) {
  return max(dates) ?? first(dates);
}

// src/services/to-date-millis.ts
import { isString as isString2 } from "@vy/string";
function toDateMillis(value) {
  if (isDate(value)) {
    return value.getTime();
  }
  if (typeof value === "number") {
    return value;
  }
  if (isString2(value)) {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? void 0 : parsed;
  }
  return void 0;
}

// src/types/api-date-schema.ts
import { oz } from "@vy/oz";
var documentationCodegen = typeof process !== "undefined" && !!process.env["API_TO_GENERATE"];
var AdvancedApiDateSchema = oz.union([oz.string().pipe(oz.coerce.date()), oz.date().openapi({ format: "date-time" })]).openapi("ApiDate", { format: "date-time" });
var BasicDateSchema = oz.date().openapi({ format: "date-time" });
var ApiDateSchema = documentationCodegen ? BasicDateSchema : AdvancedApiDateSchema;
export {
  ApiDateSchema,
  dayAfter,
  dayBefore,
  endOfDay,
  endOfMonth,
  formatDate,
  formatDateTime64,
  getMaxDate,
  getMinDate,
  isDate,
  isInTheFuture,
  isInThePast,
  isSameDate,
  now,
  startOfDay,
  startOfMonth,
  subtractDays,
  toDateMillis,
  tomorrow,
  vyDate,
  yesterday
};
